//frontend\src\components\shared\TimePicker.jsx
import { useEffect, useMemo } from "react";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

const SLOT_STEP_MINUTES = 15;
const SCHEDULE_TIME_ZONE = "Asia/Manila";

const toMinutesFromDisplayTime = (timeStr) => {
  if (!timeStr) return null;

  const twelveHourMatch = String(timeStr)
    .trim()
    .match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (twelveHourMatch) {
    let [, hour, minute, period] = twelveHourMatch;
    let hourNumber = Number(hour);

    if (period.toUpperCase() === "AM") {
      if (hourNumber === 12) hourNumber = 0;
    } else if (hourNumber !== 12) {
      hourNumber += 12;
    }

    return hourNumber * 60 + Number(minute);
  }

  const twentyFourHourMatch = String(timeStr)
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!twentyFourHourMatch) return null;

  const [, hour, minute] = twentyFourHourMatch;
  const hourNumber = Number(hour);
  const minuteNumber = Number(minute);

  if (hourNumber < 0 || hourNumber > 23 || minuteNumber < 0 || minuteNumber > 59) {
    return null;
  }

  return hourNumber * 60 + minuteNumber;
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const roundUpMinutes = (minutes, step) => Math.ceil(minutes / step) * step;

const getManilaNowInfo = () => {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: SCHEDULE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = formatter.formatToParts(new Date());
  const valueByType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const dateKey = `${valueByType.year}-${valueByType.month}-${valueByType.day}`;
  const minutes = Number(valueByType.hour) * 60 + Number(valueByType.minute);

  return { dateKey, minutes };
};

const toCanonicalDisplay = (minutes) => {
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 || 12;

  return `${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")} ${period}`;
};

const toSlotLabel = (minutes) => {
  const hour24 = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const period = hour24 >= 12 ? "pm" : "am";
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${String(minute).padStart(2, "0")}${period}`;
};

const normalizeValue = (value) => {
  const minutes = toMinutesFromDisplayTime(value);
  return minutes === null ? "" : toCanonicalDisplay(minutes);
};

const formatSelectedDateTitle = (dateStr) => {
  if (!dateStr) return "Select a date";
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
};

const TimePicker = ({
  value,
  onChange,
  selectedDate,
  onDateChange,
  minTime,
  maxTime,
  minDate,
  startMonth,
  endMonth,
}) => {
  const minAllowedMinutes = useMemo(() => toMinutesFromDisplayTime(minTime), [minTime]);
  const maxAllowedMinutes = useMemo(() => toMinutesFromDisplayTime(maxTime), [maxTime]);
  const manilaNow = getManilaNowInfo();

  const effectiveMinMinutes = useMemo(() => {
    const baseMin = minAllowedMinutes ?? 0;
    if (!selectedDate || selectedDate !== manilaNow.dateKey) return baseMin;
    return Math.max(baseMin, roundUpMinutes(manilaNow.minutes + 60, SLOT_STEP_MINUTES));
  }, [manilaNow.dateKey, manilaNow.minutes, minAllowedMinutes, selectedDate]);

  const availableSlots = useMemo(() => {
    if (!selectedDate || maxAllowedMinutes === null) return [];

    const start = effectiveMinMinutes;
    const end = maxAllowedMinutes;
    if (start > end) return [];

    const slots = [];
    for (let minute = start; minute <= end; minute += SLOT_STEP_MINUTES) {
      slots.push({
        minutes: minute,
        value: toCanonicalDisplay(minute),
        label: toSlotLabel(minute),
      });
    }
    return slots;
  }, [effectiveMinMinutes, maxAllowedMinutes, selectedDate]);

  const normalizedValue = normalizeValue(value);
  const hasValidSelectedSlot = availableSlots.some((slot) => slot.value === normalizedValue);

  useEffect(() => {
    if (!selectedDate || !value) return;
    if (!hasValidSelectedSlot) onChange("");
  }, [hasValidSelectedSlot, onChange, selectedDate, value]);

  const selectedDateObject = selectedDate ? new Date(`${selectedDate}T00:00:00`) : undefined;

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="border-b border-slate-100 px-5 py-4">
        <h4 className="text-[1.65rem] leading-tight font-bold tracking-tight text-slate-900">
          Select a Date & Time
        </h4>
      </div>

      <div className="flex flex-col md:flex-row">
        <div className="border-b border-slate-100 p-4 md:w-[52%] md:border-r md:border-b-0">
          <CalendarPicker
            mode="single"
            selected={selectedDateObject}
            onSelect={(date) => {
              if (!date) return;
              onDateChange(toDateInputValue(date));
            }}
            fromDate={minDate}
            startMonth={startMonth}
            endMonth={endMonth}
            disabled={(date) => (minDate ? date < minDate : false)}
            captionLayout="dropdown"
            className="w-full"
          />
        </div>

        <div className="p-4 md:w-[48%]">
          <p className="mb-3 text-[1.2rem] font-semibold tracking-tight text-slate-900">
            {formatSelectedDateTitle(selectedDate)}
          </p>

          {!selectedDate && (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              Select a date first to see available time slots.
            </p>
          )}

          {selectedDate && availableSlots.length === 0 && (
            <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
              No available times for this date. Please select another day.
            </p>
          )}

          {selectedDate && availableSlots.length > 0 && (
            <ScrollArea className="h-80 pr-2">
              <div className="space-y-3">
                {availableSlots.map((slot) => {
                  const isSelected = slot.value === normalizedValue;
                  return (
                    <button
                      key={slot.value}
                      type="button"
                      onClick={() => {
                        onChange(slot.value);
                      }}
                      className={cn(
                        "w-full cursor-pointer rounded-md border px-4 py-3 text-center text-[1.05rem] font-semibold transition-colors",
                        isSelected
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-blue-300 bg-white text-blue-600 hover:bg-blue-50"
                      )}>
                      {slot.label}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </section>
  );
};

export default TimePicker;
